import React, { useEffect, useMemo, useRef, useState } from 'react';

import StoreConfirmationModal from 'features/store/components/store-confirmation-modal';
import { Dimensions, GestureResponderEvent } from 'react-native';

import ActionButton from 'components/action-button';
import { CheckoutErrorsModal } from 'components/checkout-errors-modal';
import { LogUserErrorSeverity, useLogUserErrorMessage } from 'hooks/log-user-error-message';
import { useRumPageView } from 'hooks/rum/use-rum-page-view';
import { useCart } from 'hooks/use-cart';
import useDialogModal from 'hooks/use-dialog-modal';
import useFeatureMenuAddOn from 'hooks/use-feature-menu-add-on';
import useFoodware from 'hooks/use-foodware';
import { useGetPaymentErrors } from 'hooks/use-get-payment-errors';
import useIsSignUpAfterCartEnabled from 'hooks/use-is-sign-up-after-cart';
import useTraceLoading from 'hooks/use-trace-loading';
import { useIsVehicleDescriptionEnabled } from 'pages/cart/hooks/use-is-vehicle-description-enabled';
import { LegacyCondimentsOnlyModal } from 'pages/cart/legacy-condiments-only-modal';
import { useCartContext } from 'state/cart';
import { selectors, useAppSelector } from 'state/global-state';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useOrderContext } from 'state/order';
import { OrderStatus } from 'state/order/types';
import { useServiceModeContext } from 'state/service-mode';
import { useUIContext } from 'state/ui';
import { isLegacyCondimentsOnlyCart } from 'utils/legacy-condiments';
import { TraceName, stopTrace } from 'utils/performance/trace';
import { routes } from 'utils/routing';
import { isDelivery } from 'utils/service-mode';

import SignUpToContinueActionLink from './sign-up-to-continue-action-link';
import { CheckOutStickyFooter } from './sticky-footer';
import { CartPriceTooLow, SignUpToContinueDisclaimer } from './styled';
import { IPaymentProps } from './types';
import { logCartContinue } from './utils';

const Payment: React.FC<React.PropsWithChildren<IPaymentProps>> = ({
  disabled = false,
  serverOrder,
  areDeliveryDetailsValid,
  offerIsValid,
  scrollViewRef,
  phoneNumberInputRef,
  setDeliveryDetailsErrors,
  shouldSkipPriceOrder,
}) => {
  useRumPageView('payment', 'Payment');

  const { formatCurrencyForLocale } = useUIContext();

  const {
    saveVehicleDescription,
    vehicleDescription: { value: vehicleDescriptionValue, hasError: vehicleDescriptionHasError },
    orderStatus,
    priceOrderFailure,
    isOrderPriceLoading,
    isOrderPricingRetryInProgress,
    didOrderPricingRetryFail,
    tryPricing,
    rbiOrderId,
  } = useCartContext();

  const { calculateCartTotal, cartEntries } = useCart();
  const { featureFoodware } = useFoodware();
  const { sections: addOnSections } = useFeatureMenuAddOn({ ignoreEnableAddExtras: true });

  const { alertOrderDeliveryMinimum, deliveryAddress, orderPhoneNumber } = useOrderContext();
  const { serviceMode } = useServiceModeContext();

  useEffect(alertOrderDeliveryMinimum, [alertOrderDeliveryMinimum]);

  const [isCheckoutErrorsModalOpen, setIsCheckoutErrorsModalOpen] = useState(false);
  const [showCondimentsOnlyModal, setShowCondimentsOnlyModal] = useState(false);

  const [hasManualPricingBeenAttempted, setHasManualPricingAttempted] = useState(false);

  useEffect(() => {
    if (priceOrderFailure && !isOrderPricingRetryInProgress) {
      setIsCheckoutErrorsModalOpen(true);
    }
  }, [priceOrderFailure, isOrderPricingRetryInProgress]);

  const checkoutDeliveryPriceMinimum = useFlag(LaunchDarklyFlag.OVERRIDE_CHECKOUT_DELIVERY_MINIMUM);

  const enableRequireAddress2 = useFlag(
    LaunchDarklyFlag.ENABLE_DELIVERY_DETAILS_FORM_REQUIRE_ADDRESS_2
  );

  const isLoadingOffers = useAppSelector(selectors.loyalty.selectOffersLoading);
  const isSignUpAfterCartEnabled = useIsSignUpAfterCartEnabled();
  const isVehicleDescriptionEnabled = useIsVehicleDescriptionEnabled();

  const isDeliveryOrder = isDelivery(serviceMode);

  const checkoutPriceMinimum = isDeliveryOrder ? checkoutDeliveryPriceMinimum : 0;

  const cartTotal = calculateCartTotal();
  const orderTotal = isSignUpAfterCartEnabled ? cartTotal : serverOrder?.cart?.totalCents ?? 0;

  const cartSubTotal =
    isSignUpAfterCartEnabled || shouldSkipPriceOrder
      ? cartTotal
      : Number(serverOrder?.cart?.subTotalCents);

  const cartPriceTooLow = cartSubTotal < checkoutPriceMinimum;
  const foodwareItemIds = (featureFoodware?.items ?? []).flatMap(item =>
    item?._id ? [item._id] : []
  );
  const isCondimentsOnlyCart = isLegacyCondimentsOnlyCart(
    cartEntries,
    addOnSections,
    foodwareItemIds
  );

  const [StoreConfirmationDialog, , , dismissStoreConfirmationDialog] = useDialogModal({
    showCancel: true,
    Component: StoreConfirmationModal,
    modalAppearanceEventMessage: 'Confirmation Modal: Store Location',
  });

  const { shouldConfirmStoreOnCheckout } = useUIContext();

  const isLoading = (!shouldSkipPriceOrder && isOrderPriceLoading) || isLoadingOffers;

  const onStoreConfirmation = () => {
    dismissStoreConfirmationDialog();
    shouldConfirmStoreOnCheckout(true);
  };

  const { getErrorMessages } = useGetPaymentErrors({ cartPriceTooLow });

  const paymentErrorMessages = getErrorMessages(
    didOrderPricingRetryFail
      ? {
          unableToProcessOrderError: hasManualPricingBeenAttempted,
          pricedAndAvailableError: !hasManualPricingBeenAttempted,
        }
      : {
          areDeliveryDetailsWithError: !areDeliveryDetailsValid,
          invalidAddressLine2:
            enableRequireAddress2 && isDeliveryOrder && !deliveryAddress?.addressLine2,
          invalidVehicleDescription:
            isVehicleDescriptionEnabled && (vehicleDescriptionHasError || !vehicleDescriptionValue),
          invalidOfferError: !offerIsValid,
          unableToProcessOrderError: priceOrderFailure && orderStatus === OrderStatus.PRICE_ERROR,
        }
  );

  const scrolledToFocusedInput = () => {
    phoneNumberInputRef?.current?.measure(
      (x: number, y: number, width: number, height: number, pageX: number, pageY: number) => {
        const { height: windowHeight } = Dimensions.get('window');
        const scrollToYValue = pageY > 0 ? pageY : windowHeight - Math.abs(pageY);
        scrollViewRef?.current?.scrollTo({
          y: scrollToYValue,
          animated: true,
        });

        // Workaround to open keyboard on input focus.
        setTimeout(() => {
          phoneNumberInputRef?.current?.blur();
          phoneNumberInputRef?.current?.focus();
        }, 50);
      }
    );
  };

  useTraceLoading({
    name: 'Continue',
    isLoading,
  });

  const isLoadingWasTrueRef = useRef(false);
  useEffect(() => {
    if (isLoading) {
      // sometimes isLoading === false to start, guard against this
      isLoadingWasTrueRef.current = true;
    }
    if (!isLoading && isLoadingWasTrueRef.current) {
      stopTrace(TraceName.CART_LOAD_TO_CONTINUE_LOADED, { rbiOrderId: rbiOrderId ?? '' });
    }
  }, [isLoading, rbiOrderId]);

  const hasErrors = !!paymentErrorMessages.length;

  const ContinueButton = useMemo(() => {
    let buttonText = t('continue');

    if (isOrderPricingRetryInProgress) {
      buttonText = t('thisMayTakeAFewSeconds');
    } else if (isLoading) {
      buttonText = t('loadingYourOrder');
    }

    if (hasErrors) {
      const handleErrorContinueClick = () => {
        if (isCondimentsOnlyCart) {
          setShowCondimentsOnlyModal(true);
          return;
        }

        if (!areDeliveryDetailsValid && !orderPhoneNumber) {
          // Delivery Orders, set phone number error
          // when phone number input value has not changed and is empty.
          setDeliveryDetailsErrors({
            phoneNumber: t('phoneNumberRequiredError'),
          });
        }

        logCartContinue();

        setIsCheckoutErrorsModalOpen(true);
      };

      return (
        <ActionButton
          onPress={handleErrorContinueClick}
          fullWidth
          isLoading={isLoading}
          noLogOnLongLoading
          disabled={
            disabled ||
            !offerIsValid ||
            (!isCondimentsOnlyCart && cartPriceTooLow) ||
            shouldSkipPriceOrder ||
            isLoading
          }
        >
          {buttonText}
        </ActionButton>
      );
    }

    const handleContinuePress = (e: GestureResponderEvent) => {
      // if loading do nothing
      if (isLoading) {
        e?.preventDefault();
        e?.stopPropagation();
        return;
      }

      logCartContinue();

      if (isCondimentsOnlyCart) {
        setShowCondimentsOnlyModal(true);
        return;
      }

      if (isVehicleDescriptionEnabled) {
        if (!vehicleDescriptionHasError && vehicleDescriptionValue) {
          saveVehicleDescription(vehicleDescriptionValue);
        } else {
          scrollViewRef?.current?.scrollToEnd({ animated: true });
        }
      }
    };

    return (
      <ActionButton
        onPress={handleContinuePress}
        to={isCondimentsOnlyCart ? undefined : routes.cartPayment}
        fullWidth
        isLoading={isLoading}
        noLogOnLongLoading
        disabled={disabled || (!isCondimentsOnlyCart && cartPriceTooLow) || isLoading}
      >
        {buttonText}
      </ActionButton>
    );
  }, [
    isLoading,
    disabled,
    offerIsValid,
    cartPriceTooLow,
    isCondimentsOnlyCart,
    hasErrors,
    shouldSkipPriceOrder,
    areDeliveryDetailsValid,
    orderPhoneNumber,
    setDeliveryDetailsErrors,
    isVehicleDescriptionEnabled,
    vehicleDescriptionHasError,
    vehicleDescriptionValue,
    saveVehicleDescription,
    scrollViewRef,
    isOrderPricingRetryInProgress,
  ]);

  const cartPriceTooLowMessage = t('deliveryMinumumNotReached', {
    min: formatCurrencyForLocale(checkoutDeliveryPriceMinimum),
  });

  // TODO FIX technically message visibility should be tied to isLoading (it flashes "minimum not reached" on page load every time)
  // but trying to not change any page logic with these error message additions to reduce risk
  // bug here: https://rbictg.atlassian.net/browse/BKPE-2611
  useLogUserErrorMessage({
    message: cartPriceTooLowMessage,
    logWhenTrue: cartPriceTooLow && !isLoading,
    severity: LogUserErrorSeverity.Recoverable,
  });

  const CTAButton = isSignUpAfterCartEnabled ? (
    <>
      <SignUpToContinueDisclaimer>{t('signUpToContinueDisclaimer')}</SignUpToContinueDisclaimer>
      <SignUpToContinueActionLink />
    </>
  ) : (
    ContinueButton
  );

  const priceTooLowCmp = cartPriceTooLow && !isLoading && (
    <CartPriceTooLow>{cartPriceTooLowMessage}</CartPriceTooLow>
  );

  const attemptManualOrderPricing = () => {
    setHasManualPricingAttempted(true);
    tryPricing({ isRepriceAttempt: true });
    setIsCheckoutErrorsModalOpen(false);
  };

  const shouldAllowManualPricingAttempt =
    didOrderPricingRetryFail && !hasManualPricingBeenAttempted;

  return (
    <>
      <CheckOutStickyFooter
        priceTooLowCmp={priceTooLowCmp}
        CTAButton={CTAButton}
        isLoading={isLoading}
        orderTotal={orderTotal}
      />
      {/* modals */}
      <StoreConfirmationDialog
        onConfirm={onStoreConfirmation}
        onDismiss={dismissStoreConfirmationDialog}
      />
      {isCheckoutErrorsModalOpen && (
        <CheckoutErrorsModal
          {...(shouldAllowManualPricingAttempt && {
            onCtaPress: attemptManualOrderPricing,
            ctaText: t('tryAgain'),
            heading: t('oopsSomethingWentWrong'),
          })}
          checkoutErrors={paymentErrorMessages}
          onClose={() => {
            setIsCheckoutErrorsModalOpen(false);
            scrolledToFocusedInput();
          }}
        />
      )}
      <LegacyCondimentsOnlyModal
        isOpen={showCondimentsOnlyModal}
        onClose={() => setShowCondimentsOnlyModal(false)}
      />
    </>
  );
};

export default Payment;
